import { CaretDown, Check, Tag } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';

/**
 * Filtro multi-select de Estado de Venta (Formalizado, Entregado, Reservado,
 * Disponible). Es un filtro TEMPORAL: el backend evalúa el estado de cada obra
 * según el MES del dato (histórico), no su estado actual. Acá solo se elige qué
 * estados incluir; la dimensión temporal la resuelve el endpoint.
 */
interface Props {
  /** Estados disponibles (de /api/estados-venta). */
  disponibles: string[];
  /** Estados seleccionados. */
  seleccion: string[];
  onChange: (estados: string[]) => void;
}

export function FiltroEstadoVenta({ disponibles, seleccion, onChange }: Props) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  const sel = new Set(seleccion);
  const toggle = (estado: string) => {
    const s = new Set(sel);
    if (s.has(estado)) s.delete(estado);
    else s.add(estado);
    onChange([...s]);
  };

  const resumen = sel.size === 0 ? 'Todos' : sel.size === 1 ? [...sel][0]! : `${sel.size} estados`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className={`flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-sm transition ${
          sel.size > 0
            ? 'border-blue-400 bg-blue-50 text-blue-700'
            : 'border-input bg-background text-foreground hover:bg-zinc-50'
        }`}
        title="Estado de venta según el mes del dato (histórico)."
      >
        <Tag className="h-3.5 w-3.5" weight="bold" />
        <span className="max-w-[120px] truncate text-muted-foreground">{resumen}</span>
        <CaretDown className="h-3 w-3" weight="bold" />
      </button>

      {abierto && (
        <div className="absolute left-0 z-50 mt-1 w-52 rounded-md border border-zinc-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2 text-xs">
            <span className="font-medium text-muted-foreground">Estado de venta</span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-blue-600 hover:underline"
            >
              Limpiar
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {disponibles.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">Sin estados.</p>
            ) : (
              disponibles.map((estado) => (
                <button
                  key={estado}
                  type="button"
                  onClick={() => toggle(estado)}
                  className="flex w-full items-center gap-1.5 px-3 py-1.5 text-xs hover:bg-zinc-50"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      sel.has(estado)
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-zinc-300 bg-white'
                    }`}
                  >
                    {sel.has(estado) && <Check className="h-3 w-3" weight="bold" />}
                  </span>
                  {estado}
                </button>
              ))
            )}
          </div>
          <p className="border-t border-zinc-100 px-3 py-1.5 text-[10px] text-muted-foreground">
            El estado se evalúa según el mes de cada dato.
          </p>
        </div>
      )}
    </div>
  );
}
